import { useEffect, useState } from "react";
import { api, inr, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [resyncing, setResyncing] = useState("");
  const load = () => api.get("/admin/invoices").then((r) => setInvoices(r.data));
  useEffect(() => { load(); }, []);

  const resync = async (id) => {
    setResyncing(id);
    try {
      await api.post(`/admin/zoho/resync/${id}`);
      toast.success("Resynced to Zoho");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setResyncing(""); }
  };

  const del = async (id) => {
    if (!window.confirm("Move this invoice to trash?")) return;
    try { await api.post(`/admin/invoices/${id}/soft-delete`); toast.success("Moved to trash"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <h1 className="text-3xl font-heading font-semibold tracking-tight mb-6" data-testid="admin-invoices-title">All invoices</h1>
      <div className="border border-gray-200">
        <div className="grid grid-cols-12 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wider text-gray-600 font-medium">
          <div className="col-span-2">Invoice #</div>
          <div className="col-span-3">Customer</div>
          <div className="col-span-2">Date</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2">Zoho</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-1"/>
        </div>
        {invoices.map((inv) => (
          <div key={inv.id} className="grid grid-cols-12 px-4 py-3 border-b border-gray-100 items-center text-sm" data-testid={`admin-inv-${inv.id}`}>
            <div className="col-span-2 font-medium">{inv.zoho?.zoho_invoice_number || `#${inv.id.slice(-6)}`}</div>
            <div className="col-span-3 truncate text-gray-700">{inv.user_email}</div>
            <div className="col-span-2 text-gray-500">{new Date(inv.created_at).toLocaleDateString()}</div>
            <div className="col-span-1"><span className={`status-pill status-unpaid`}>{inv.status}</span></div>
            <div className="col-span-2">
              <span className={`status-pill ${inv.zoho?.mode === "live" ? "status-synced" : inv.zoho?.status === "failed" ? "status-failed" : "status-mock"}`}>
                {inv.zoho?.status === "failed" ? "failed" : inv.zoho?.mode || "pending"}
              </span>
            </div>
            <div className="col-span-1 text-right font-medium">{inr(inv.total)}</div>
            <div className="col-span-1 flex justify-end gap-1">
              <button onClick={() => resync(inv.id)} disabled={resyncing === inv.id} className="text-gray-400 hover:text-[#002fa7] p-1" title="Re-sync to Zoho" data-testid={`resync-${inv.id}`}>
                <RefreshCw className={`w-4 h-4 ${resyncing === inv.id ? "animate-spin" : ""}`} strokeWidth={1.5}/>
              </button>
              <button onClick={() => del(inv.id)} className="text-gray-400 hover:text-red-600 p-1" title="Move to trash" data-testid={`invoice-delete-${inv.id}`}>
                <Trash2 className="w-4 h-4" strokeWidth={1.5}/>
              </button>
              <Link to={`/invoices/${inv.id}`} className="text-xs text-[#002fa7] hover:underline">View</Link>
            </div>
          </div>
        ))}
        {invoices.length === 0 && <div className="p-8 text-sm text-gray-500">No invoices yet.</div>}
      </div>
    </div>
  );
}
